import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { useParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { Trash2Icon } from 'lucide-react';
import { DateField } from '@/components/DateField';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useApi, useNavigate } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { cn } from '@/lib/utils';
import { mceTypeBadgeClass, statusBadgeClass } from '@/components/statusBadges';
import { capitalize as capitalizeJs, isEmpty as isEmptyJs, normalizeCamelCase as normalizeCamelCaseJs, uniqueId as uniqueIdJs } from '../../ui/utils';
import { quartzTheme } from '../../ui/components/Grid/themes';
import { currencyFormatter as currencyFormatterJs } from '../QuotePage/content/QuoteGrids/utils';
import { usePolicyPageContext as usePolicyPageContextJs } from './context/PolicyPageContext';
import { ActionButton, ActionRow, ActionSpacer, Grid, IncludeInFormsCheckbox, type Row } from './tabParts';

/* eslint-disable @typescript-eslint/no-explicit-any -- boundary: the untyped JavaScript helpers and context below, and AG Grid's cell params */
const capitalize = capitalizeJs as (value: string) => string;
const isEmpty = isEmptyJs as (value: unknown) => boolean;
const normalizeCamelCase = normalizeCamelCaseJs as (value: string) => string;
const uniqueId = uniqueIdJs as () => string;
const currencyFormatter = currencyFormatterJs as any as (args: { field: unknown; withDecimals?: boolean }) => string;
type CellParams = any;
type PolicyPageState = {
  MCEsData: Row[];
  policy: Row;
  formSchedule?: Row[];
  savedFormSchedule?: Row[];
  setSavedFormSchedule: (schedule: Row[]) => void;
  endorsements?: Row[];
  monthlyStatements?: Row[];
};
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
/* eslint-enable @typescript-eslint/no-explicit-any */

/** A status or MCE-type chip in the MCEs list, in the chip colours of constants.js (D5). */
export function MceStatusBadge({ value, className, adjustment }: { value: string; className: string; adjustment?: { amount?: number } }) {
  return (
    <div className="mt-[3px] flex flex-row items-center">
      <Badge className={cn('mx-2 my-0.5 h-auto gap-1.5 rounded-md px-2 py-0.5 text-xs font-bold', className)}>{value}</Badge>
      {!!adjustment?.amount && <span className="ml-2 text-xs font-bold text-muted-note italic">(Adjusted)</span>}
    </div>
  );
}

const sortByEffectiveAt = (schedule: Row[]) => schedule.sort((a, b) => Number(new Date(a.effectiveAt || 0)) - Number(new Date(b.effectiveAt || 0)));

const CreateSchedule = ({
  showScheduleModal,
  setShowScheduleModal,
  policyId,
  savedFormSchedule,
  setSavedFormSchedule,
  formSchedule = [],
}: {
  showScheduleModal: boolean;
  setShowScheduleModal: (open: boolean) => void;
  policyId: string;
  savedFormSchedule?: Row[] | undefined;
  setSavedFormSchedule: (schedule: Row[]) => void;
  formSchedule?: Row[];
}) => {
  const [rowData, setRowData] = useState<Row[]>([]);
  const { downloadFormSchedule, saveFormSchedule, getFormSchedule } = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleDeleteRow = (formId: string) => {
    setRowData((prevData) => prevData.filter((row) => row.formId !== formId));
  };

  const columnDefs: ColDef[] = [
    {
      headerName: 'Form/Endorsement Number',
      field: 'code',
      editable: true,
      width: 280,
      wrapText: true,
      cellStyle: {
        textAlign: 'left',
        verticalAlign: 'top',
      },
      rowDrag: true,
    },
    {
      headerName: 'Name',
      field: 'name',
      editable: true,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        textAlign: 'left',
        verticalAlign: 'center',
        whiteSpace: 'pre-wrap',
        lineHeight: '2.5',
      },
      cellEditor: 'agLargeTextCellEditor',
    },
    {
      headerName: '',
      field: 'actions',
      cellRenderer: (params: CellParams) => (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Delete row"
          className="text-destructive"
          onClick={() => handleDeleteRow(params.data.formId)}
        >
          <Trash2Icon />
        </Button>
      ),
      width: 60,
    },
  ];

  useEffect(() => {
    const fetchAndCombineSchedule = async () => {
      if (savedFormSchedule && savedFormSchedule.length > 0) {
        setRowData(
          savedFormSchedule.map((form) => ({
            ...form,
            formId: uniqueId(),
          }))
        );
      } else {
        try {
          const defaultSchedule: Row[] | undefined = await getFormSchedule(policyId);
          const sortedData = sortByEffectiveAt([...(defaultSchedule || []), ...formSchedule]);
          setRowData(
            sortedData.map((form) => ({
              ...form,
              formId: form.formId || uniqueId(),
            }))
          );
        } catch (err) {
          console.error('Error fetching default schedule:', err);
          setRowData([{ formId: uniqueId(), code: '', name: '' }]);
        }
      }
    };

    fetchAndCombineSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFormSchedule, formSchedule, policyId]);

  const handleAddRow = () => {
    const newRow = { formId: uniqueId(), code: '', name: '' };
    setRowData((prevData) => (Array.isArray(prevData) ? [...prevData, newRow] : [newRow]));
  };

  const handleCellValueChanged = (e: CellParams) => {
    const updatedData = rowData.map((row) => (row.formId === e.data.formId ? { ...row, [e.colDef.field]: e.newValue } : row));
    setRowData(updatedData);
  };

  const handleOnSubmit = async () => {
    const requestBody = rowData.map(({ code, name }) => ({ code, name }));
    setIsLoading(true);
    try {
      const response = await downloadFormSchedule(policyId, requestBody);
      if (response) {
        const downloadUrl = response.value;
        window.open(downloadUrl, '_blank');
        setShowScheduleModal(false);
      }
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsLoading(false);
      setShowScheduleModal(false);
    }
  };
  const handleRowDragEnd = (event: CellParams) => {
    const newOrder: Row[] = [];
    event.api.forEachNodeAfterFilterAndSort((node: CellParams) => {
      newOrder.push(node.data);
    });
    setRowData(newOrder);
  };

  const handleProcessDataFromClipboard = useCallback(
    (params: CellParams) => {
      const pastedCellsData: string[][] = params.data;

      if (pastedCellsData.length === 0) {
        return [];
      }

      if (pastedCellsData.length > 1) {
        const focusedCell = params.api.getFocusedCell();
        const currentTargetRowIndex = focusedCell ? focusedCell.rowIndex : -1;

        if (currentTargetRowIndex === rowData.length - 1) {
          const newRowDataSet = [...rowData];

          const lastRow = { ...newRowDataSet[currentTargetRowIndex] };
          const firstPastedRow = pastedCellsData[0] as string[];

          if (firstPastedRow[0] !== undefined) {
            lastRow.code = firstPastedRow[0].replace(/\\n/g, '\n').trim();
          }
          if (firstPastedRow[1] !== undefined) {
            lastRow.name = firstPastedRow[1].replace(/\\n/g, '\n').trim();
          }
          newRowDataSet[currentTargetRowIndex] = lastRow;

          for (let i = 1; i < pastedCellsData.length; i++) {
            const pastedLine = pastedCellsData[i] as string[];
            newRowDataSet.push({
              formId: uniqueId(),
              code: pastedLine[0]?.replace(/\\n/g, '\n').trim() || '',
              name: pastedLine[1]?.replace(/\\n/g, '\n').trim() || '',
            });
          }

          setRowData(newRowDataSet);
          return [firstPastedRow];
        }
      }

      return pastedCellsData.map((row) => [row[0]?.replace(/\\n/g, '\n').trim() || '', row[1]?.replace(/\\n/g, '\n').trim() || '']);
    },
    [rowData]
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = rowData.map(({ code, name }, index) => ({
        code,
        name,
        policyId: policyId,
        rank: index,
      }));
      const response = await saveFormSchedule(policyId, payload);
      setSavedFormSchedule(response);
      notifySuccess('Schedule saved successfully');
      setShowScheduleModal(false);
    } catch (error) {
      console.error('Save failed:', error);
      notifyError('Failed to save schedule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const defaultSchedule: Row[] | undefined = await getFormSchedule(policyId);
      const sortedData = sortByEffectiveAt([...(defaultSchedule || []), ...formSchedule]);
      setRowData(
        sortedData.map((form) => ({
          ...form,
          formId: form.formId || uniqueId(),
        }))
      );
    } catch (err) {
      console.error('Error resetting schedule:', err);
      setRowData([{ formId: uniqueId(), code: '', name: '' }]);
    }
  };

  return (
    <Dialog
      open={showScheduleModal}
      onOpenChange={(open) => {
        if (!open) setShowScheduleModal(false);
      }}
    >
      <DialogContent className="w-[90%] max-w-[90%] sm:max-w-[90%]">
        <DialogHeader>
          <DialogTitle className="capitalize">Create Schedule Page</DialogTitle>
        </DialogHeader>
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="grid-container max-h-[500px] min-h-[400px] w-full overflow-y-auto rounded-lg border border-schedule-border shadow-sm">
            <AgGridReact
              theme={quartzTheme}
              columnMenu="legacy"
              rowData={rowData}
              columnDefs={columnDefs}
              rowHeight={80}
              onCellValueChanged={handleCellValueChanged}
              domLayout="normal"
              rowDragManaged={true}
              cellSelection={true}
              onRowDragEnd={handleRowDragEnd}
              processDataFromClipboard={handleProcessDataFromClipboard}
              processCellForClipboard={({ value }) => {
                return value?.toString().replace(/\n/g, '\\n') || value;
              }}
              processCellFromClipboard={({ value }) => {
                return value?.toString().replace(/\\n/g, '\n') || value;
              }}
            />
          </div>
          <ActionRow className="mt-4 pt-6">
            <ActionButton className="m-0 w-[150px]" onClick={handleAddRow}>
              + Add Row
            </ActionButton>
            <ActionButton className="m-0 w-[150px]" onClick={handleReset}>
              Reset
            </ActionButton>
          </ActionRow>
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button variant="outline" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner /> : null}
            Save
          </Button>
          <Button onClick={handleOnSubmit} disabled={isLoading}>
            {isLoading ? <Spinner /> : null}
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Starts an MCE on a chosen date: a location change, or a legacy one. */
const StartMCEDialog = ({
  title,
  showModal,
  setShowModal,
  onSubmit,
}: {
  title: string;
  showModal: boolean;
  setShowModal: (open: boolean) => void;
  onSubmit: (effectiveAt: string) => Promise<void>;
}) => {
  const [effectiveDate, setEffectiveDate] = useState(() => moment());
  const [inProgress, setInProgress] = useState(false);

  const handleOnSubmit = async () => {
    setInProgress(true);
    try {
      await onSubmit(effectiveDate.format('YYYY-MM-DD'));
    } finally {
      setShowModal(false);
      setInProgress(false);
    }
  };

  return (
    <Dialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open) setShowModal(false);
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="capitalize">{title}</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          {/* DateField edge: the picked calendar date becomes moment(date), which the payload formats. */}
          <DateField
            label="Effective At"
            value={new Date(effectiveDate.year(), effectiveDate.month(), effectiveDate.date())}
            onChange={(date) => date && setEffectiveDate(moment(date))}
          />
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button onClick={handleOnSubmit} disabled={inProgress}>
            {inProgress ? <Spinner /> : null}
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManageMCEModal = ({ showModal, setShowModal, policyId }: { showModal: boolean; setShowModal: (open: boolean) => void; policyId: string }) => {
  const { updatePolicyMCEs } = useApi();
  const navigate = useNavigate();

  const submit = async (effectiveAt: string) => {
    try {
      const response = await updatePolicyMCEs(policyId, {
        type: 'locationChange',
        effectiveAt,
      });
      const newMCEId = response[0]?.id;
      if (newMCEId) {
        navigate.to(`/policies/${policyId}/mces/${newMCEId}`);
      } else {
        console.error('Failed to retrieve the MCE ID after creation');
        notifyError('An error occurred: Unable to retrieve the MCE ID.');
      }
    } catch (error) {
      console.error('Failed to update monthly reporting data:', error);
      notifyError('Unable to Update the MCE, Please try again later');
    }
  };

  return <StartMCEDialog title="create Location Change MCE" showModal={showModal} setShowModal={setShowModal} onSubmit={submit} />;
};

const LegacyMCEModal = ({ showModal, setShowModal, policyId }: { showModal: boolean; setShowModal: (open: boolean) => void; policyId: string }) => {
  const { createLegacyMCE } = useApi();
  const navigate = useNavigate();

  const submit = async (effectiveAt: string) => {
    try {
      const response = await createLegacyMCE(policyId, {
        type: 'legacy',
        effectiveAt,
      });
      const newMCEId = response?.id;
      if (newMCEId) {
        const path = `/policies/${policyId}/mces/${newMCEId}`;
        const search = '?legacy=true';
        navigate.to({ pathname: path, search });
      } else {
        notifyError('An error occurred: Unable to retrieve the MCE ID.');
      }
    } catch (error) {
      console.error('Failed to  create the MCE:', error);
      notifyError('Unable to create the MCE, Please try again later');
    }
  };

  return <StartMCEDialog title="Create Legacy MCE" showModal={showModal} setShowModal={setShowModal} onSubmit={submit} />;
};

const negativeInRed = (params: CellParams) => (params.value < 0 ? { color: 'red' } : null);

const PolicyGridData = (props: { setGridReference: (ref: unknown) => void }) => {
  const navigate = useNavigate();
  const { setGridReference } = props;
  const { policyId = '' } = useParams<{ policyId: string }>();
  const [selectedMceId, setSelectedMceId] = useState<string | null>(null);
  const [MCEsList, setMCEsList] = useState<Row[]>([]);
  const {
    MCEsData,
    policy,
    formSchedule,
    savedFormSchedule,
    setSavedFormSchedule,
    endorsements = [],
    monthlyStatements = [],
  } = usePolicyPageContext();
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  const [isDownloadingBreakDown, setIsDownloadingBreakDown] = useState(false);
  const [isDownloadingUwwb, setIsDownloadingUwwb] = useState(false);

  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [policyMCEs, setPolicyMCEs] = useState<Row[]>([]);
  const executedMCEs = policyMCEs.filter((mce) => mce.status === 'executed');
  const shouldShowExecutedMCEs = true; // Provide the ability to enable or disable the visibility of executed MCEs in the Create Schedule modal.

  const mceFormEndorsements = endorsements?.filter((endorsement) => endorsement.endorsementType === 'MceForm') || [];
  const [shouldShowCheckbox, setShouldShowCheckbox] = useState<boolean>();
  const [includeChecked, setIncludeChecked] = useState(false);
  useEffect(() => {
    if (mceFormEndorsements.length < executedMCEs.length) {
      setShouldShowCheckbox(true);
    } else {
      setShouldShowCheckbox(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mceFormEndorsements, executedMCEs]);

  const mcesIncludeInPolicyForms = executedMCEs.filter((executed) => {
    return !mceFormEndorsements.some((endorsement) => String(executed.id) === String(endorsement?.endorsementData?.mce_id));
  });

  function formatDate(dateString: string) {
    const [year, month, day] = dateString.split('-').map(Number) as [number, number, number];
    const date = new Date(year, month - 1, day);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: '2-digit' };
    const formattedDate = date.toLocaleDateString('en-US', options);

    return formattedDate.toUpperCase();
  }

  const policyEffectiveUntil = moment.utc(policy.effectiveUntil).format('YYYY-MM-DD');
  const MCEFormCode = 'SKWD UND 01 17 07 23';
  const updatedFormSchedules = [
    ...(formSchedule || []),
    ...((shouldShowExecutedMCEs &&
      [...executedMCEs, ...policyMCEs.filter((mce) => mce.status === 'released')].map((mce) => ({
        formId: mce.id,
        code: MCEFormCode,
        effectiveAt: mce.effectiveAt,
        name: `MISCELLANEOUS CHANGE ENDORSEMENT -\nINVENTORY SCHEDULE FLUCTUATION -\n${formatDate(mce.effectiveAt)} - ${formatDate(
          mce.effectiveUntil || policyEffectiveUntil
        )} -\n${mce.mceNumber}`,
      }))) ||
      []),
  ];

  const [showModal, setShowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const {
    downloadMCE,
    deleteMCE,
    getPolicyMCEs,
    downloadBreakdown,
    downloadMonthlyStatementById,
    createEndorsement,
    deleteLegacyMCE,
    getPolicyLegacyMCEs,
    generateMceWorkbook,
  } = useApi();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [showLegacyModal, setShowLegacyModal] = useState(false);

  const toggleParentRow = (parentId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  // Today's list builds no fee columns: useMemo calls this with no argument.
  const getColDef = useMemo(
    () => {
      const colDefs: Row[] = [
        {
          headerName: '',
          maxWidth: 60,
          suppressRowClickSelection: true,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          },
          cellRenderer: (params: CellParams) => {
            const isExpanded = expandedRows[params.data.id];
            const hasSuperseded = !!params.data.supersedes;

            return (
              <div className="flex h-full w-full cursor-pointer flex-row items-center justify-center gap-2 text-base text-mce-expander">
                <div className="pl-[15px]">
                  <input
                    type="radio"
                    aria-label={`Select ${params.data.mceNumber}`}
                    name="mceId"
                    checked={selectedMceId === params.data.id}
                    onChange={() => setSelectedMceId(params.data.id)}
                    className="m-0 scale-[1.3] cursor-pointer accent-primary"
                  />
                </div>
                <button
                  type="button"
                  aria-label={isExpanded ? 'Hide superseded MCE' : 'Show superseded MCE'}
                  aria-expanded={!!isExpanded}
                  onClick={() => toggleParentRow(params.data.id)}
                  className={cn(
                    'mb-[3px] transition-[transform,color] duration-300',
                    hasSuperseded ? 'visible' : 'invisible',
                    isExpanded ? 'text-mce-expander-open' : 'text-mce-expander'
                  )}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              </div>
            );
          },
        },

        {
          field: 'mceNumber',
          headerName: 'MCE Number',
          suppressRowClickSelection: true,
          minWidth: 280,
        },
        {
          field: 'status',
          headerName: 'Status',
          minWidth: 180,
          cellRenderer: ({ data: { status, relationshipType, id } }: CellParams) => {
            // Check if this MCE has an Inventory Value Endorsement
            const hasIVE = endorsements?.some((e) => {
              if (e?.endorsementType !== 'invEndorsement') return false;
              const parent = e?.endorsementData?.parent;
              return parent?.type === 'mce' && String(parent?.id) === String(id);
            });

            return (
              <div className="flex flex-row items-center gap-2">
                <MceStatusBadge value={capitalize(status)} className={statusBadgeClass(capitalize(status))} />
                {hasIVE && <MceStatusBadge value="IVE" className={statusBadgeClass('IVE')} />}
                {relationshipType === 'reversed' && <span className="ml-2 text-xs text-muted-note italic">(Reversed)</span>}
              </div>
            );
          },
        },

        {
          field: 'executedAt',
          headerName: 'Executed Date',
          minWidth: 130,
          valueFormatter: ({ data: { executedAt } }: CellParams) =>
            executedAt ? moment(executedAt, 'MMM D, YYYY, h:mm:ss A').format('YYYY-MM-DD') : 'N/A',
        },
        {
          field: 'type',
          headerName: 'Type',
          minWidth: 150,
          cellRenderer: ({ data: { type } }: CellParams) => <MceStatusBadge value={normalizeCamelCase(type)} className={mceTypeBadgeClass(type)} />,
        },
        {
          field: 'boundTIV',
          headerName: 'Pre-Bound TIV',
          minWidth: 140,

          valueFormatter: (params: CellParams) => {
            const value = params.data.boundTIV || '';
            return currencyFormatter({ field: { value }, withDecimals: true });
          },
          cellStyle: negativeInRed,
        },
        {
          field: 'coveredValue',
          headerName: 'Post-Bound TIV',
          minWidth: 140,
          valueFormatter: (params: CellParams) => {
            const value = params.data.coveredValue || '';
            return currencyFormatter({ field: { value }, withDecimals: true });
          },
          cellStyle: negativeInRed,
        },

        {
          field: 'effectiveAt',
          headerName: 'Effective Date',
          minWidth: 130,
          valueFormatter: ({ data: { effectiveAt } }: CellParams) => (effectiveAt ? effectiveAt : 'N/A'),
        },
        {
          field: 'effectiveUntil',
          headerName: 'Effective Until',
          minWidth: 130,

          valueFormatter: ({ data: { effectiveUntil } }: CellParams) => (effectiveUntil ? effectiveUntil : 'N/A'),
        },
        {
          field: 'netPremium',
          headerName: 'Premium',
          minWidth: 130,
          valueFormatter: (params: CellParams) => {
            return !isEmpty(params.data) ? currencyFormatter({ field: params, withDecimals: true }) : '';
          },
          cellStyle: negativeInRed,
        },
        {
          field: 'notes',
          headerName: 'Notes',
          minWidth: 150,
          maxWidth: 200,
          valueFormatter: (params: CellParams) => {
            const value = params.data.notes || '';
            return value.length > 35 ? `${value.slice(0, 35)}...` : value;
          },
        },
        {
          headerName: 'MCE Incl Adjustment',
          field: 'mceIncludedAdjustments',
          minWidth: 150,
          valueFormatter: (params: CellParams) => {
            return params?.data?.mceIncludedAdjustments ? currencyFormatter({ field: params, withDecimals: true }) : '';
          },
          cellStyle: negativeInRed,
        },
      ];
      return colDefs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedMceId, expandedRows]
  );

  useEffect(() => {
    setPolicyMCEs(MCEsData);
  }, [MCEsData]);

  const handleDownloadMCE = async () => {
    setIsDownloadInProgress(true);
    try {
      if (selectedMceId) {
        const response = await downloadMCE(selectedMceId, policyId);
        if (response && response.request && response.request.responseURL) {
          const downloadUrl = response.request.responseURL;
          window.open(downloadUrl, '_blank');
        } else {
          notifyError('Failed to download the MCE. Please try again.');
        }
      } else {
        notifyError('Please select an MCE to download.');
      }
    } catch (error) {
      notifyError('An error occurred while trying to download the MCE. Please try again.');
      console.error(error);
    } finally {
      setIsDownloadInProgress(false);
      setSelectedMceId(null);
    }
  };

  const selectedMce = MCEsList.find((mce) => mce.id === selectedMceId);
  const isSelectedQuarterly = selectedMce?.type === 'quarterly';

  const handleDownloadBreakdown = async () => {
    setIsDownloadingBreakDown(true);
    try {
      if (!selectedMceId) {
        notifyError('Please select an MCE.');
        return;
      }

      if (isSelectedQuarterly) {
        const statement = monthlyStatements.find((s) => s.mceId === selectedMceId);
        if (!statement) {
          notifyError('No monthly statement found for this quarterly MCE.');
          return;
        }
        const response = await downloadMonthlyStatementById(policyId, statement.id);
        if (response && response.request && response.request.responseURL) {
          window.open(response.request.responseURL, '_blank');
        } else {
          notifyError('Failed to download the monthly statement. Please try again.');
        }
      } else {
        const response = await downloadBreakdown(selectedMceId, policyId);
        if (response && response.request && response.request.responseURL) {
          window.open(response.request.responseURL, '_blank');
        } else {
          notifyError('Failed to download the MCE breakdown. Please try again.');
        }
      }
    } catch (error) {
      notifyError('An error occurred while trying to download. Please try again.');
      console.error(error);
    } finally {
      setIsDownloadingBreakDown(false);
      setSelectedMceId(null);
    }
  };

  const handleDeleteMCE = async () => {
    setIsDeleteInProgress(true);
    try {
      if (selectedMceId) {
        const selectedMCE = MCEsList.find((mce) => mce.id === selectedMceId);
        if (selectedMCE?.type === 'legacy') {
          await deleteLegacyMCE(policyId, selectedMceId);
        } else {
          await deleteMCE(policyId, selectedMceId);
        }
        const mces = await getPolicyMCEs(policyId);
        const legacyMCEs = await getPolicyLegacyMCEs(policyId);
        const MCEs = [...mces, ...legacyMCEs];
        setPolicyMCEs(MCEs);
      } else {
        notifyError('Please select an MCE to download.');
      }
    } catch (error) {
      notifyError('An error occurred while trying to download the MCE. Please try again.');
    } finally {
      setIsDeleteInProgress(false);
      setSelectedMceId(null);
    }
  };

  const handleCellClicked = (params: CellParams) => {
    if (params.colDef.headerName === '') return;
    const mceId = params.data?.id;

    if (mceId) {
      setTimeout(() => {
        const isLegacy = params.data?.type === 'legacy';
        const path = `/policies/${policyId}/mces/${mceId}`;
        const search = isLegacy ? '?legacy=true' : '';

        navigate.to({ pathname: path, search });
      }, 100);
    }
  };

  useEffect(() => {
    const mappedMCEs = policyMCEs
      .sort((a, b) => Number(new Date(b.effectiveAt)) - Number(new Date(a.effectiveAt)))
      .map((mce): Row => {
        const fees: Record<string, { amount: unknown }> = {};
        mce.fees.forEach((fee: Row) => {
          fees[fee.name] = { amount: fee.amount };
        });
        return {
          ...mce,
          fees,
          // Add null check for calculations
          boundTIV:
            mce.calculations?.reduce((sum: number, value: Row) => {
              return sum + (value.boundInventory || 0);
            }, 0) || 0,
          mceIncludedAdjustments: mce.adjustment?.amount ? parseFloat(mce.netPremium) + parseFloat(mce?.adjustment?.amount) : 0,
        };
      });

    const parentMCEs = mappedMCEs.filter((mce) => mce.status !== 'superseded' || mce.reverses);

    const updatedRowData = parentMCEs.map((mce): Row => {
      const childMCEs = mappedMCEs.filter((child) => {
        return child.id === mce.supersedes;
      });
      return {
        ...mce,
        children: childMCEs.map((child) => ({
          ...child,
          relationshipType: mce.reverses ? 'reversed' : 'superseded',
        })),
      };
    });

    const flattenedRowData = updatedRowData.reduce<Row[]>((acc, mce) => {
      acc.push(mce);
      if (expandedRows[mce.id]) {
        acc.push(...mce.children);
      }
      return acc;
    }, []);
    setMCEsList(flattenedRowData);
  }, [expandedRows, policyMCEs]);

  const handleDownloadMceUwwb = async () => {
    if (!selectedMceId) {
      notifyError('Please select a locationChange MCE to download.');
      return;
    }

    const selected = MCEsList.find((mce) => mce.id === selectedMceId);

    if (!selected || selected.type !== 'locationChange') {
      notifyError('Only locationChange MCEs can be downloaded as UWWB.');
      return;
    }

    setIsDownloadingUwwb(true);
    try {
      await generateMceWorkbook(policy.id, selected.id);
    } catch (err) {
      console.error(err);
      notifyError('Failed to download the UWWB. Please try again.');
    } finally {
      setIsDownloadingUwwb(false);
    }
  };

  const handleIncludeInPolicyForms = async () => {
    try {
      const mce = mcesIncludeInPolicyForms.find((m) => {
        return m.id === selectedMceId;
      });
      if (mce) {
        const payload = {
          endorsementType: 'MceForm',
          endorsementData: {
            mce_id: selectedMceId,
            effectiveAt: mce?.effectiveAt,
            effectiveUntil: mce?.effectiveUntil,
          },
        };
        await createEndorsement(policy.id, payload);
        notifySuccess('Saved Successfully.');
      } else {
        notifyError('MCE is already included or cannot be in Policy Forms');
      }
      setShouldShowCheckbox(false);
    } catch (error) {
      console.error('Error inserting mces in policy forms:', error);
      notifyError('An error occurred while inserting mces in policy forms. Please try again.');
    }
  };

  return (
    <div>
      <div className="grid-container max-h-[600px] overflow-y-auto">
        <Grid
          data={MCEsList}
          columns={getColDef}
          setGridReference={setGridReference}
          rowSelection="multiple"
          shouldShowPagination={false}
          columnWidth={150}
          floatingFilter={false}
          onCellClicked={handleCellClicked}
        />
      </div>
      {showModal && <ManageMCEModal showModal={showModal} setShowModal={setShowModal} policyId={policyId} />}
      {showScheduleModal && (
        <CreateSchedule
          showScheduleModal={showScheduleModal}
          setShowScheduleModal={setShowScheduleModal}
          policyId={policyId}
          formSchedule={updatedFormSchedules}
          savedFormSchedule={savedFormSchedule}
          setSavedFormSchedule={setSavedFormSchedule}
        />
      )}
      {showLegacyModal && <LegacyMCEModal showModal={showLegacyModal} setShowModal={setShowLegacyModal} policyId={policyId} />}

      <div className="flex min-h-[70px] flex-wrap justify-center bg-white">
        <div className="flex w-full flex-row flex-wrap items-center self-stretch p-2 pl-0">
          {!!MCEsList.length && (
            <>
              <ActionButton onClick={handleDownloadMCE} loading={isDownloadInProgress}>
                Download MCE
              </ActionButton>
              <ActionButton onClick={handleDownloadBreakdown} loading={isDownloadingBreakDown}>
                {isSelectedQuarterly ? 'Download Monthly Statement' : 'Download Breakdown'}
              </ActionButton>
              <ActionButton onClick={handleDownloadMceUwwb} loading={isDownloadingUwwb}>
                Download UWWB
              </ActionButton>
            </>
          )}
          <ActionButton onClick={() => setShowModal(true)}>Create Location Change MCE</ActionButton>
          <ActionButton onClick={() => setShowScheduleModal(true)}>Create Schedule Page</ActionButton>
          <ActionButton onClick={() => setShowLegacyModal(true)}>Create Legacy MCE</ActionButton>
          <ActionSpacer />
          {shouldShowCheckbox && (
            <IncludeInFormsCheckbox
              checked={includeChecked}
              onChange={(checked) => {
                setIncludeChecked(checked);
                if (checked) {
                  handleIncludeInPolicyForms();
                }
              }}
            />
          )}
          {!!MCEsList.length && (
            <ActionButton tone="danger" className="ml-auto border-action-border" onClick={handleDeleteMCE} loading={isDeleteInProgress}>
              Delete MCE
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  );
};

export const MiscPolicyDetails = (props: { setGridReference: (ref: unknown) => void }) => {
  return <PolicyGridData {...props} />;
};
